import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TextField } from "@mui/material";
import { IMAGE_PATH, instanceToken } from "../../../utils/constant";
import onboardingService from "../../../services/onboardings";
import imageUpload from "../../../services/upload";
import { CreateOnboardingSchema } from "../../../schema/CreateOnboardingSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  EditPageLayout,
  EditImageSection,
  EditFieldLabel,
  EditFormTopSection,
  EditFormParagraphsSection,
} from "../../../components/edit/EditPageLayout";

const imgFileTypes = ["image/gif", "image/jpeg", "image/png", "image/svg+xml"];

export default function CreateOnboarding({ homeAlert }) {
  const navigate = useNavigate();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageFileUrl, setImageFileUrl] = useState("");
  const [values, setValues] = useState({ image_url: "" });
  const [, setOnboardings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CreateOnboardingSchema),
    initialValues: {
      name_en: "",
      name_mm: "",
      description: "",
      description_mm: "",
      link: "",
    },
  });

  async function getImageUrl(imgData) {
    const response = await imageUpload.getImageUrl(
      instanceToken.token,
      imgData
    );
    const result = response.data;
    const path = IMAGE_PATH + result?.imageName;
    setImageFileUrl(result?.imageUploadUrl);
    setValues({
      ...values,
      image_url: path,
    });
  }

  const changeFile = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const img = e.target.files[0];
      if (!imgFileTypes.includes(img.type)) {
        setErrorMessage({
          ...errorMessage,
          image_url: "Please select image. (PNG, JPG, JPEG, GIF, ...)",
        });
        return;
      }
      if (img.size > 204800) {
        setErrorMessage({
          ...errorMessage,
          image_url: "Image file size must be smaller than 200KB.",
        });
        return;
      }
      setImageFile(img);
      setImagePreview(URL.createObjectURL(img));
      getImageUrl(img);
    }
  };

  const formData = new FormData();

  const handleCreate = async (data) => {
    formData.append("name_en", data?.name_en);
    formData.append("name_mm", data?.name_mm);
    formData.append("link", data?.link);
    formData.append("description", data?.description);
    formData.append("description_mm", data?.description_mm);
    formData.append("image_data", "");
    formData.append("image_url", values?.image_url);

    try {
      setLoading(true);
      await imageUpload.uploadImage(imageFileUrl, imageFile);
      const response = await onboardingService.postOnboarding(
        instanceToken.token,
        formData
      );
      navigate("/onboardings");
      setOnboardings(response.data);
      homeAlert("New Onboardings have been created.", false);
    } catch (error) {
      console.log(error);
      homeAlert("Error on server.", true);
    } finally {
      setLoading(false);
      setErrorMessage({});
    }
  };

  return (
    <EditPageLayout
      title="Create Onboarding"
      subtitle="Add onboarding details and content in both languages."
      breadcrumbs={[
        { label: "Onboarding", path: "/onboardings" },
        { label: "Create" },
      ]}
      listPath="/onboardings"
      loading={loading}
      submitLabel="Create"
      onSubmit={handleSubmit(handleCreate)}
    >
      <EditFormTopSection
        image={
          <EditImageSection
            uploadLabel="Onboarding Image"
            previewLabel="Image Preview"
            imagePreview={imagePreview}
            onFileChange={changeFile}
            error={errorMessage["image_url"] || errorMessage["image_data"]}
            recommendedSize="1920 x 1200 px (16:10)"
          />
        }
      >
        <div>
          <EditFieldLabel required>Name (English)</EditFieldLabel>
          <TextField
            fullWidth
            id="name_en"
            {...register("name_en")}
            error={!!errors.name_en}
            helperText={errors.name_en?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Name (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            id="name_mm"
            {...register("name_mm")}
            error={!!errors.name_mm}
            helperText={errors.name_mm?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Link</EditFieldLabel>
          <TextField
            fullWidth
            id="link"
            {...register("link")}
            error={!!errors.link}
            helperText={errors.link?.message}
          />
        </div>
      </EditFormTopSection>

      <EditFormParagraphsSection>
        <div>
          <EditFieldLabel required>Description (English)</EditFieldLabel>
          <TextField
            fullWidth
            multiline
            minRows={4}
            id="description"
            {...register("description")}
            error={!!errors.description}
            helperText={errors.description?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Description (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            multiline
            minRows={4}
            id="description_mm"
            {...register("description_mm")}
            error={!!errors.description_mm}
            helperText={errors.description_mm?.message}
          />
        </div>
      </EditFormParagraphsSection>
    </EditPageLayout>
  );
}
